/**
 * DailyBlast engine — Pine-exact port of JSR_DailyBlast.txt.
 *
 * Single source of truth. Byte-identical copy lives at
 * backend/src/services/strategies/dailyBlastEngine.ts.
 *
 * Pure — no React / chart / DB deps. Consumers:
 *   • Chart overlay (utils/strategies/dailyBlast.ts) — renders candleColors + markers.
 *   • Scan service  (backend/src/services/dailyBlast.service.ts) — writes signals.
 *
 * Section numbers map to Pine line ranges in JSR_DailyBlast.txt.
 */

export type CandleType =
  | 'Regular'
  | 'HA'
  | 'Custom Smooth HA'
  | 'Custom Smooth Linreg'
  | 'Custom Linreg HA';

export type FourState = 'dark_green' | 'light_green' | 'dark_red' | 'light_red';
export type Strength = 'HIGH' | 'MEDIUM' | 'LOW';
export type Action = 'BUY' | 'SHORT';

export interface Bar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
}

export interface DailyBlastSignal {
  action: Action;
  time: number;
  entry: number;
  isl: number;
  target: number;
  strength: Strength;
  confirmed: boolean;
  /** Plot-level anchor for the marker (low for BUY, high for SHORT) in the
   *  selected candle type. Used by the chart layer for precise arrow placement. */
  anchor: number;
  state: FourState;
}

export interface DailyBlastOutput {
  /** 4-state classification per bar (null until LR window warm). */
  states: (FourState | null)[];
  /** Per-bar plot low/high for the selected candle type (for marker anchoring). */
  plotLow: (number | null)[];
  plotHigh: (number | null)[];
  /** Confirmed signals (one per close where a trigger fired). */
  confirmedSignals: DailyBlastSignal[];
  /** Live-candidate signal for the last bar (CONFIRMED=false), if any. */
  liveSignal: DailyBlastSignal | null;
}

export interface EnginePineOptions {
  lrLen?: number;        // 14
  emaLen?: number;       // 10 (on linreg → SLR HA components)
  tgtMode?: 'ATR × multiplier' | 'R-multiple';
  tgtAtrMult?: number;   // 2.0
  tgtRMult?: number;     // 2.0
  atrLen?: number;       // 14
  combinedUpper?: (number | null)[];
  combinedLower?: (number | null)[];
  combinedMid?: (number | null)[];
  candleType?: CandleType; // default 'HA' for plot anchors
}

const DEFAULTS: Required<Omit<EnginePineOptions, 'combinedUpper' | 'combinedLower' | 'combinedMid'>> = {
  lrLen: 14,
  emaLen: 10,
  tgtMode: 'R-multiple',
  tgtAtrMult: 2.0,
  tgtRMult: 2.0,
  atrLen: 14,
  candleType: 'HA',
};

/* ============================================================================
 * Basic series primitives — kept local to the engine for parity isolation.
 * ========================================================================== */

function ema(src: (number | null)[], period: number): (number | null)[] {
  const n = src.length;
  const out: (number | null)[] = new Array(n).fill(null);
  const k = 2 / (period + 1);
  let prev: number | null = null;
  let sum = 0, cnt = 0;
  for (let i = 0; i < n; i++) {
    const v = src[i];
    if (prev == null) {
      if (v == null) continue;
      sum += v; cnt++;
      if (cnt === period) { prev = sum / period; out[i] = prev; }
    } else {
      if (v == null) { out[i] = prev; continue; }
      prev = v * k + prev * (1 - k);
      out[i] = prev;
    }
  }
  return out;
}

/** Pine ta.linreg(src, len, 0): value at bar = offset-0 end point of least-squares fit. */
function linreg(src: number[], period: number): (number | null)[] {
  const n = src.length;
  const out: (number | null)[] = new Array(n).fill(null);
  for (let i = period - 1; i < n; i++) {
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    let bad = false;
    for (let k = 0; k < period; k++) {
      const y = src[i - period + 1 + k];
      if (y == null || !Number.isFinite(y)) { bad = true; break; }
      const x = k;
      sumX += x; sumY += y; sumXY += x * y; sumX2 += x * x;
    }
    if (bad) continue;
    const denom = period * sumX2 - sumX * sumX;
    if (denom === 0) { out[i] = src[i]; continue; }
    const slope = (period * sumXY - sumX * sumY) / denom;
    const intercept = (sumY - slope * sumX) / period;
    out[i] = slope * (period - 1) + intercept;
  }
  return out;
}

function trueRange(bars: Bar[]): number[] {
  const out: number[] = new Array(bars.length).fill(0);
  for (let i = 0; i < bars.length; i++) {
    const h = bars[i].high, l = bars[i].low;
    const pc = i > 0 ? bars[i - 1].close : bars[i].close;
    out[i] = Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc));
  }
  return out;
}

function rma(src: number[], period: number): (number | null)[] {
  // Pine ta.atr uses RMA (Wilder's). RMA = EMA with alpha = 1/period.
  const n = src.length;
  const out: (number | null)[] = new Array(n).fill(null);
  if (n < period) return out;
  let sum = 0;
  for (let i = 0; i < period; i++) sum += src[i];
  let prev = sum / period;
  out[period - 1] = prev;
  for (let i = period; i < n; i++) {
    prev = (prev * (period - 1) + src[i]) / period;
    out[i] = prev;
  }
  return out;
}

/* ============================================================================
 * Section 1 — Smoothed HA (Pine 206–210)
 * ========================================================================== */

interface HA { o: number; c: number; h: number; l: number; }

function computeSmoothedHA(bars: Bar[]): (HA | null)[] {
  const src = bars.map(b => (b.open + b.high + b.low + b.close) / 4);
  const haCloseArr = ema(src, 3);
  const out: (HA | null)[] = new Array(bars.length).fill(null);
  let prev: HA | null = null;
  for (let i = 0; i < bars.length; i++) {
    const hc = haCloseArr[i];
    if (hc == null) { prev = null; continue; }
    const ho = prev == null
      ? (bars[i].open + bars[i].close) / 2
      : (prev.o + prev.c) / 2;
    const hh = Math.max(bars[i].high, ho, hc);
    const hl = Math.min(bars[i].low, ho, hc);
    const cur: HA = { o: ho, c: hc, h: hh, l: hl };
    out[i] = cur;
    prev = cur;
  }
  return out;
}

/* ============================================================================
 * Section 2 — Custom Smooth HA (Pine 212–214)
 * ========================================================================== */

function computeCustomSmoothHA(haSmooth: (HA | null)[]): {
  o: (number | null)[];
  c: (number | null)[];
} {
  const haOpen = haSmooth.map(h => h ? h.o : null);
  const haClose = haSmooth.map(h => h ? h.c : null);
  return { o: ema(haOpen, 10), c: ema(haClose, 10) };
}

/* ============================================================================
 * Section 3 — Smoothed Linreg HA (Pine 216–227) — drives 4-state classification
 * ========================================================================== */

interface SlrHa {
  o2: (number | null)[];
  c2: (number | null)[];
}

function computeSmoothedLinregHA(bars: Bar[], lrLen: number): SlrHa {
  const opens = bars.map(b => b.open);
  const closes = bars.map(b => b.close);
  const highs = bars.map(b => b.high);
  const lows = bars.map(b => b.low);

  const lrO = linreg(opens, lrLen);
  const lrC = linreg(closes, lrLen);

  const slrO = ema(lrO, 10);
  const slrC = ema(lrC, 10);
  const slrH = ema(highs, 10);
  const slrL = ema(lows, 10);

  const haClose: (number | null)[] = new Array(bars.length).fill(null);
  for (let i = 0; i < bars.length; i++) {
    if (slrO[i] == null || slrC[i] == null || slrH[i] == null || slrL[i] == null) continue;
    haClose[i] = (slrO[i]! + slrH[i]! + slrL[i]! + slrC[i]!) / 4;
  }

  // Recursive slr_haopen — seed (_slr_o + _slr_c) / 2
  const haOpen: (number | null)[] = new Array(bars.length).fill(null);
  for (let i = 0; i < bars.length; i++) {
    if (haClose[i] == null) { continue; }
    const prevOpen = haOpen[i - 1];
    const prevClose = haClose[i - 1];
    if (prevOpen == null || prevClose == null) {
      haOpen[i] = (slrO[i]! + slrC[i]!) / 2;
    } else {
      haOpen[i] = (prevOpen + prevClose) / 2;
    }
  }

  return { o2: ema(haOpen, 10), c2: ema(haClose, 10) };
}

/* ============================================================================
 * Section 4 — 4-state classification (Pine 235–242)
 * ========================================================================== */

function computeFourState(slr: SlrHa): (FourState | null)[] {
  const n = slr.o2.length;
  const body: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const a = slr.o2[i], b = slr.c2[i];
    if (a == null || b == null) continue;
    body[i] = Math.abs(b - a);
  }
  const out: (FourState | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const a = slr.o2[i], b = slr.c2[i];
    if (a == null || b == null) continue;
    const prevBody = i > 0 ? body[i - 1] : null;
    const curBody = body[i]!;
    const isBull = b >= a;
    const isExp = prevBody == null ? true : curBody > prevBody;
    if (isBull && isExp) out[i] = 'dark_green';
    else if (!isBull && !isExp) out[i] = 'light_green';   // prepare-for-long
    else if (!isBull && isExp) out[i] = 'dark_red';
    else out[i] = 'light_red';                             // prepare-for-short
  }
  return out;
}

/* ============================================================================
 * Section 5 — Plot low/high for selected candle type (Pine 254–258)
 * ========================================================================== */

function computePlotLowHigh(
  bars: Bar[],
  haSmooth: (HA | null)[],
  customSmoothHA: { o: (number | null)[]; c: (number | null)[] },
  slr: SlrHa,
  candleType: CandleType,
): { plotLow: (number | null)[]; plotHigh: (number | null)[] } {
  const n = bars.length;
  const plotLow: (number | null)[] = new Array(n).fill(null);
  const plotHigh: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    if (candleType === 'Regular') {
      plotLow[i] = bars[i].low;
      plotHigh[i] = bars[i].high;
    } else if (candleType === 'HA') {
      const ha = haSmooth[i];
      if (!ha) continue;
      plotLow[i] = ha.l;
      plotHigh[i] = ha.h;
    } else if (candleType === 'Custom Smooth HA') {
      const o = customSmoothHA.o[i], c = customSmoothHA.c[i];
      if (o == null || c == null) continue;
      plotLow[i] = Math.min(bars[i].low, o, c);
      plotHigh[i] = Math.max(bars[i].high, o, c);
    } else if (candleType === 'Custom Smooth Linreg') {
      const o = slr.o2[i], c = slr.c2[i];
      if (o == null || c == null) continue;
      plotLow[i] = Math.min(bars[i].low, o, c);
      plotHigh[i] = Math.max(bars[i].high, o, c);
    } else {
      // Custom Linreg HA  = avg of custom-smooth-HA and SLR-HA, bounded by real HL
      const sho = customSmoothHA.o[i], shc = customSmoothHA.c[i];
      const slo = slr.o2[i], slc = slr.c2[i];
      if (sho == null || shc == null || slo == null || slc == null) continue;
      const o = (sho + slo) / 2;
      const c = (shc + slc) / 2;
      plotLow[i] = Math.min(bars[i].low, o, c);
      plotHigh[i] = Math.max(bars[i].high, o, c);
    }
  }
  return { plotLow, plotHigh };
}

/* ============================================================================
 * Section 6 — Strength grading (Pine 466–474)
 * ========================================================================== */

function gradeStrength(
  isBuy: boolean,
  pivHi: number,
  pivLo: number,
  upper: number | null,
  mid: number | null,
  lower: number | null,
): Strength {
  if (upper == null || mid == null || lower == null) return 'HIGH';
  if (isBuy) {
    if (pivHi >= upper) return 'HIGH';
    if (pivHi > mid) return 'MEDIUM';
    return 'LOW';
  } else {
    if (pivLo <= lower) return 'HIGH';
    if (pivLo < mid) return 'MEDIUM';
    return 'LOW';
  }
}

/* ============================================================================
 * Section 7 — Target (Pine 477–480)
 * ========================================================================== */

function computeTarget(
  isBuy: boolean,
  entry: number,
  isl: number,
  atr: number | null,
  mode: 'ATR × multiplier' | 'R-multiple',
  atrMult: number,
  rMult: number,
): number {
  const risk = Math.abs(entry - isl);
  const add = mode === 'ATR × multiplier'
    ? (atr != null ? atrMult * atr : rMult * risk)
    : rMult * risk;
  return isBuy ? entry + add : entry - add;
}

/* ============================================================================
 * Section 8 — Main engine (Pine prepare-state + trigger + SL breach, 429–563)
 * ========================================================================== */

export function runDailyBlast(
  bars: Bar[],
  opts: EnginePineOptions = {},
): DailyBlastOutput {
  const cfg = { ...DEFAULTS, ...opts };
  const n = bars.length;
  if (n < cfg.lrLen + 10) {
    return {
      states: new Array(n).fill(null),
      plotLow: new Array(n).fill(null),
      plotHigh: new Array(n).fill(null),
      confirmedSignals: [],
      liveSignal: null,
    };
  }

  // 1–3. Derived series
  const haSmooth = computeSmoothedHA(bars);
  const customSmoothHA = computeCustomSmoothHA(haSmooth);
  const slr = computeSmoothedLinregHA(bars, cfg.lrLen);
  const states = computeFourState(slr);
  const { plotLow, plotHigh } = computePlotLowHigh(bars, haSmooth, customSmoothHA, slr, cfg.candleType);

  // ATR for target calc
  const tr = trueRange(bars);
  const atrArr = rma(tr, cfg.atrLen);

  // Envelope bounds (optional, supplied by caller for strength grading)
  const upper = opts.combinedUpper ?? new Array(n).fill(null);
  const lower = opts.combinedLower ?? new Array(n).fill(null);
  const mid = opts.combinedMid ?? new Array(n).fill(null);

  // 9. Prepare-state machine + trigger walk
  let prepState: -1 | 0 | 1 = 0;
  let tDir: -1 | 0 | 1 = 0;
  let tEntry: number | null = null;
  let tIsl: number | null = null;

  const confirmed: DailyBlastSignal[] = [];
  let liveSignal: DailyBlastSignal | null = null;

  for (let i = 2; i < n; i++) {
    const stateCur = states[i];
    const statePrev = states[i - 1];
    if (!stateCur || !statePrev) continue;

    const haCur = haSmooth[i];
    if (!haCur) continue;

    // Prepare transitions — Pine 438-439
    const prepShortStart = stateCur === 'light_red' && statePrev === 'dark_green';
    const prepLongStart = stateCur === 'light_green' && statePrev === 'dark_red';

    // 2-candle extremes — Pine 442-445. Uses [i-1] and [i-2].
    const ha1 = haSmooth[i - 1], ha2 = haSmooth[i - 2];
    const prev2HaHigh = Math.max(
      ha1?.h ?? haCur.h,
      ha2?.h ?? haCur.h,
    );
    const prev2HaLow = Math.min(
      ha1?.l ?? haCur.l,
      ha2?.l ?? haCur.l,
    );
    const prev2High = Math.max(bars[i - 1].high, bars[i - 2].high);
    const prev2Low = Math.min(bars[i - 1].low, bars[i - 2].low);

    // Triggers — Pine 448-449
    const longTrigger = prepState === 1 && haCur.h > prev2HaHigh;
    const shortTrigger = prepState === -1 && haCur.l < prev2HaLow;

    // SL breach to opposite side — Pine 452-453
    const longSlBreachToShort =
      tDir === 1 && tIsl != null && bars[i].close < tIsl && !shortTrigger;
    const shortSlBreachToLong =
      tDir === -1 && tIsl != null && bars[i].close > tIsl && !longTrigger;

    // Same-bar exclusivity — Pine 456-457 (BUY wins ties)
    const wouldBuy = longTrigger || shortSlBreachToLong;
    const wouldShort = (shortTrigger || longSlBreachToShort) && !wouldBuy;

    // Candidates — Pine 460-463
    const candBuyEntry = shortSlBreachToLong ? bars[i].close : prev2HaHigh;
    const candBuyIsl = prev2Low;
    const candShortEntry = longSlBreachToShort ? bars[i].close : prev2HaLow;
    const candShortIsl = prev2High;

    const atrI = atrArr[i];
    const uBound = upper[i] ?? null;
    const mBound = mid[i] ?? null;
    const lBound = lower[i] ?? null;

    // Commit state on "confirmed" (i.e. the bar is closed — we treat every bar
    // except the last as confirmed here; live evaluation for i===n-1 is below).
    const isLast = i === n - 1;
    if (!isLast) {
      if (prepShortStart) prepState = -1;
      if (prepLongStart) prepState = 1;

      if (wouldBuy) {
        const strength = gradeStrength(true, haCur.h, haCur.l, uBound, mBound, lBound);
        const target = computeTarget(true, candBuyEntry, candBuyIsl, atrI, cfg.tgtMode, cfg.tgtAtrMult, cfg.tgtRMult);
        confirmed.push({
          action: 'BUY',
          time: bars[i].time,
          entry: candBuyEntry,
          isl: candBuyIsl,
          target,
          strength,
          confirmed: true,
          anchor: plotLow[i] ?? bars[i].low,
          state: stateCur,
        });
        tDir = 1; tEntry = candBuyEntry; tIsl = candBuyIsl; prepState = 0;
      } else if (wouldShort) {
        const strength = gradeStrength(false, haCur.h, haCur.l, uBound, mBound, lBound);
        const target = computeTarget(false, candShortEntry, candShortIsl, atrI, cfg.tgtMode, cfg.tgtAtrMult, cfg.tgtRMult);
        confirmed.push({
          action: 'SHORT',
          time: bars[i].time,
          entry: candShortEntry,
          isl: candShortIsl,
          target,
          strength,
          confirmed: true,
          anchor: plotHigh[i] ?? bars[i].high,
          state: stateCur,
        });
        tDir = -1; tEntry = candShortEntry; tIsl = candShortIsl; prepState = 0;
      }
    } else {
      // Live-candidate for last (unclosed) bar — Pine 500-519.
      if (wouldBuy) {
        const strength = gradeStrength(true, haCur.h, haCur.l, uBound, mBound, lBound);
        const target = computeTarget(true, candBuyEntry, candBuyIsl, atrI, cfg.tgtMode, cfg.tgtAtrMult, cfg.tgtRMult);
        liveSignal = {
          action: 'BUY',
          time: bars[i].time,
          entry: candBuyEntry,
          isl: candBuyIsl,
          target,
          strength,
          confirmed: false,
          anchor: plotLow[i] ?? bars[i].low,
          state: stateCur,
        };
      } else if (wouldShort) {
        const strength = gradeStrength(false, haCur.h, haCur.l, uBound, mBound, lBound);
        const target = computeTarget(false, candShortEntry, candShortIsl, atrI, cfg.tgtMode, cfg.tgtAtrMult, cfg.tgtRMult);
        liveSignal = {
          action: 'SHORT',
          time: bars[i].time,
          entry: candShortEntry,
          isl: candShortIsl,
          target,
          strength,
          confirmed: false,
          anchor: plotHigh[i] ?? bars[i].high,
          state: stateCur,
        };
      }
    }
    // Silence unused-var noise: tEntry is state carried between bars for SL breach.
    void tEntry;
  }

  return { states, plotLow, plotHigh, confirmedSignals: confirmed, liveSignal };
}
